import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ACCESS_LEVEL_RANK, AccessLevel, PermissionGrants, resolveGrant } from 'app/models/permission.model';

/** How long a loaded permission map is used before navigation fetches it again */
const MAX_AGE_MS = 60 * 1000;
const STORAGE_KEY = 'permissions';
/** Built-in customer levels (User, AnnaPerennaMainUser, Editor, MainUser, CustomerAdmin), as the API's BuiltInRoles */
const CUSTOMER_LEVELS = [0, 40, 50, 100, 101];

interface StoredPermissions {
  owner: string;
  fetchedAt: number;
  grants: PermissionGrants;
}

/**
 * Raised by the HTTP interceptor when the API answers 403. Kept separate from PermissionService so
 * the interceptor doesn't depend on a service that itself makes HTTP calls.
 */
@Injectable({ providedIn: 'root' })
export class PermissionEvents {
  readonly forbidden$ = new Subject<void>();
}

/**
 * The logged in user's permissions (GET /api/Permissions/Me) and the rules for reading them.
 *
 * UX only: the API is the authority. Resolution is most-specific-grant-wins, exactly as in Lyra-API:
 * "Devices.Firmware" uses its own grant if the map has one, otherwise the grant on "Devices",
 * otherwise None. A grant on a child never satisfies a check on its parent.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly grantsSubject = new BehaviorSubject<PermissionGrants | null>(null);
  private pending: Observable<PermissionGrants> | null = null;
  /** Has the map been fetched from the API since the Portal was loaded */
  private fetchedSincePageLoad = false;

  /** Emits whenever the permission map changes */
  readonly changes: Observable<PermissionGrants | null> = this.grantsSubject.asObservable();

  constructor(private api: ApiService, events: PermissionEvents) {
    const stored = this.readStored();
    if (stored && stored.owner === this.currentOwner()) {
      this.grantsSubject.next(stored.grants);
    }

    // A 403 means the map may be stale (a role was edited): fetch it again
    events.forbidden$.subscribe(() => this.load(true).subscribe());
  }

  /**
   * Load the map, from the cache when it belongs to the current user and is fresh.
   * Never fails: without a map the user simply has no permissions.
   */
  load(force = false): Observable<PermissionGrants> {
    const stored = this.readStored();
    const owner = this.currentOwner();
    if (!owner) {
      this.grantsSubject.next({});
      return of({});
    }

    // The stored map renders the first page at once, but the first check after a page load always
    // fetches: a role may have changed since, and a refresh is how users pick that up
    if (!force && this.fetchedSincePageLoad && stored && stored.owner === owner && Date.now() - stored.fetchedAt < MAX_AGE_MS) {
      if (this.grantsSubject.value === null) this.grantsSubject.next(stored.grants);
      return of(stored.grants);
    }

    if (!this.pending) {
      this.pending = this.api.getMyPermissions().pipe(
        map(grants => grants ?? {}),
        tap(grants => {
          this.fetchedSincePageLoad = true;
          this.store({ owner, fetchedAt: Date.now(), grants });
          this.grantsSubject.next(grants);
        }),
        catchError(() => {
          // Keep whatever we had; the API still enforces access
          return of(this.grantsSubject.value ?? {});
        }),
        finalize(() => this.pending = null),
        shareReplay(1));
    }
    return this.pending;
  }

  /** Forget the cached map, e.g. after the user's own role was changed */
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.grantsSubject.next(null);
  }

  /** Resolved access level of an area */
  resolve(area: string): AccessLevel {
    const grants = this.grantsSubject.value;
    return grants ? resolveGrant(grants, area) : 'None';
  }

  /** Does the user have at least the given level on the area */
  has(area: string, level: AccessLevel = 'Read'): boolean {
    return ACCESS_LEVEL_RANK[this.resolve(area)] >= ACCESS_LEVEL_RANK[level];
  }

  /**
   * Does the user have at least the given grants' access on every area, as the API's StaffPermissions.Covers:
   * nobody gives more than they have. Levels only change at explicitly granted keys, so comparing the
   * keys of both maps covers every area.
   */
  covers(grants: PermissionGrants): boolean {
    const keys = new Set([...Object.keys(grants ?? {}), ...Object.keys(this.grantsSubject.value ?? {})]);
    return [...keys].every(key => ACCESS_LEVEL_RANK[resolveGrant(grants ?? {}, key)] <= ACCESS_LEVEL_RANK[this.resolve(key)]);
  }

  /** Customer levels get their access from a role; every other level is a tier with code-defined access */
  static isCustomerLevel(roleLevel: number): boolean {
    return CUSTOMER_LEVELS.includes(roleLevel);
  }

  /**
   * Replacement for an inline `roleLevel >= X` check, as the API's HasPermissionOrTierLevelAsync: customer
   * users need the permission (so the check also works for custom roles), tiers keep the old level rule.
   */
  hasOrTierLevel(area: string, level: AccessLevel, tierMinimumLevel: number, roleLevel: number): boolean {
    return PermissionService.isCustomerLevel(roleLevel) ? this.has(area, level) : roleLevel >= tierMinimumLevel;
  }

  /** Does the user have any access on the area or one of its children. For menus, never for gating actions. */
  hasAnyUnder(area: string): boolean {
    if (this.has(area, 'Read')) return true;
    const grants = this.grantsSubject.value ?? {};
    return Object.keys(grants).some(key => key.startsWith(area + '.') && this.has(key, 'Read'));
  }

  private currentOwner(): string | null {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.userId ? `${user.server}|${user.customerId}|${user.userId}` : null;
    } catch {
      return null;
    }
  }

  private readStored(): StoredPermissions | null {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private store(value: StoredPermissions): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }
}
